<template>
  <div :class="['event-info', singleEventPageModifier]">
    <BaseNuxtImage
      v-if="event.logo"
      class="event-info__logo"
      :src="event.logo.filename"
      :alt="event.title"
    />
    <h4 v-if="hasTitle" class="event-info__title">
      {{ eventTitle }}
    </h4>
    <div>
      <p class="event-info__details">
        <span>{{ $t('common.events.date') }}</span>
        <span>{{ event.date }}</span>
      </p>
      <p class="event-info__details">
        <span>{{ $t('common.events.location') }}</span>
        <span>{{ event.location }}</span>
      </p>
      <p v-if="hasBooth" class="event-info__details">
        <span>{{ $t('common.events.booth') }}</span>
        <span>{{ event.booth }}</span>
      </p>
      <p v-if="event.speech" class="event-info__details">
        <span>{{ $t('common.events.speech') }}</span>
        <span>{{ event.speech }}</span>
      </p>
      <p v-if="isEventPresentation" class="event-info__details">
        <span>{{ $t('common.events.presentation') }}</span>
        <BaseRichText :rich-text-template="event.presentation" />
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
// TODO: Add height and/or width property to Image in Storyblok

import type { EventStoryblok } from '@/types'
import { EventContextType } from '@/types'

interface Props {
  event: EventStoryblok
  context?: EventContextType
}
const props = withDefaults(defineProps<Props>(), {
  context: EventContextType.BASE,
})

const isEventPresentation = computed(() => props.event.presentation && JSON.stringify(props.event.presentation).includes("\"text\""))

const hasBooth = computed(() => !!props.event.booth)

const singleEventPageModifier = computed(() => {
  if (props.context === EventContextType.SINGLE_EVENT) {
    return 'event-info--event-page'
  }

  if (props.context === EventContextType.FEATURED) {
    return 'event-info--featured'
  }

  return ''
})

const eventTitle = computed(() =>
  props.context === EventContextType.SINGLE_EVENT
    ? props.event.hero_title
    : props.event.title,
)

const hasTitle = computed(() => !!eventTitle.value?.length)
</script>

<style lang="scss" scoped>
.event-info {
  $self: &;

  display: grid;
  gap: 48px;

  &__image {
    width: min(200px, 100%);
    margin-bottom: var(--space24);

    img {
      height: 100%;
    }

    &--card {
      margin-bottom: var(--space32);

      @media (min-width: $breakpoint-lg) {
        margin-bottom: var(--space56);
      }
    }
  }

  &__title {
    margin: 0;

    @include heading-4;

    @media (min-width: $breakpoint-lg) {
      @include heading-3;
    }

    &--featured {
      @include heading-3;

      @media (min-width: $breakpoint-sm) {
        @include heading-2;
      }

      @media (min-width: $breakpoint-lg) {
        @include heading-1;
      }
    }
  }

  &__details {
    display: grid;
    grid-template-columns: 120px 1fr;
    padding: var(--space16) 0;
    margin: 0;
    border-bottom: 1px solid var(--gray-400);
    gap: 10px;
    max-width: 450px;

    @media (min-width: $breakpoint-md) {
      max-width: 375px;
    }

    &:first-of-type {
      border-top: 1px solid var(--gray-400);
    }

    span:first-of-type {
      font-weight: var(--font-weight-paragraph-bold);
    }
  }

  &--event-page {
    #{$self}__image {
      margin-bottom: 0;
    }
  }

  &--featured {
    #{ $self }__title {
      @include heading-3;

      @media (min-width: $breakpoint-lg) {
        @include heading-2;
      }
    }

    #{$self}__image {
      margin-bottom: 43px;

      @media (min-width: $breakpoint-lg) {
        margin-bottom: var(--space56);
      }
    }
  }
}
</style>
